import { useEffect, useMemo, useState } from 'react'
import { Toggle } from '../../components/Toggle'
import { TrashIcon } from '../../components/icons/TrashIcon'
import { PencilIcon } from '../../components/icons/PencilIcon'
import { WarningIcon } from '../../components/icons/WarningIcon'
import { PeopleIcon } from '../../components/icons/PeopleIcon'
import { Trans, useTranslation } from 'react-i18next'
import { useMutation, useQueryClient } from '@tanstack/react-query'
import { useApiFetch, ApiError } from '../../lib/api'
import { Card } from '../../components/Card'
import { Avatar } from '../../components/Avatar'
import { ConfirmDialog } from '../../components/ConfirmDialog'
import { splitOpsForTree } from './opsTreeData'
import { PrincipalSearch } from '../../components/PrincipalSearch'
import { OperationsTree } from '../../components/OperationsTree'
import { UnsavedChangesDialog } from '../../components/UnsavedChangesDialog'
import { SuccessToast } from '../../components/SuccessToast'
import { useEntityEditorRegistration } from '../../hooks/useEntityEditorRegistration'
import { InfoTooltip } from '../../components/InfoTooltip'
import type {
  PermissionTemplate,
  UserAccess,
  UserAccessCreate,
  UserAccessUpdate,
  UserAccessOperation,
  MambuConnectionOperation,
  PrincipalSearchItem,
} from '../../lib/types'

export interface UsersTabProps {
  connectionId: string
  templates: PermissionTemplate[]
  userAccess: UserAccess[]
  tenantOperations: MambuConnectionOperation[]
  defaultPermissionTemplateId: string | null
  onDefaultPermissionTemplateIdChange: (next: string | null) => void
}

/* ─── Card 1: Default User Access ─── */

interface DefaultUserAccessCardProps {
  templates: PermissionTemplate[]
  defaultPermissionTemplateId: string | null
  onDefaultPermissionTemplateIdChange: (next: string | null) => void
}

function DefaultUserAccessCard({ templates, defaultPermissionTemplateId, onDefaultPermissionTemplateIdChange }: DefaultUserAccessCardProps) {
  const { t } = useTranslation()
  const isOn = defaultPermissionTemplateId !== null
  // Keep the last selected template so the greyed-out select still shows a
  // sensible value when the toggle is off — turning back on restores the pick.
  const [lastSelectedTemplateId, setLastSelectedTemplateId] = useState<string>(
    () => defaultPermissionTemplateId ?? templates[0]?.id ?? '',
  )

  useEffect(() => {
    if (defaultPermissionTemplateId !== null) {
      setLastSelectedTemplateId(defaultPermissionTemplateId)
    }
  }, [defaultPermissionTemplateId])

  function handleToggle(next: boolean) {
    if (next) {
      onDefaultPermissionTemplateIdChange(lastSelectedTemplateId || null)
    } else {
      onDefaultPermissionTemplateIdChange(null)
    }
  }

  function handleSelect(newId: string) {
    setLastSelectedTemplateId(newId)
    onDefaultPermissionTemplateIdChange(newId)
  }

  return (
    <Card
      title={t('mambuMcpDetail.users.defaultAccess.cardTitle')}
      subtitle={t('mambuMcpDetail.users.defaultAccess.cardSubtitle')}
    >
      <div className="toggle-row" style={{ padding: 0 }}>
        <label className="toggle-row-label" style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
          <span>{t('mambuMcpDetail.users.defaultAccess.toggleLabel')}</span>
          <InfoTooltip width={300} placement="below">
            {t('mambuMcpDetail.users.defaultAccess.infoTooltip')}
          </InfoTooltip>
        </label>

        <div style={{ minWidth: 220, maxWidth: 300, transition: 'opacity .15s', opacity: isOn ? 1 : 0.5, flexShrink: 0 }}>
          <select
            className="form-select"
            aria-label={t('mambuMcpDetail.users.defaultAccess.toggleLabel')}
            value={lastSelectedTemplateId}
            disabled={!isOn}
            onChange={(e) => handleSelect(e.target.value)}
          >
            {templates.length === 0 && (
              <option value="">{t('mambuMcpDetail.users.defaultAccess.noTemplatesOption')}</option>
            )}
            {templates.map(tpl => (
              <option key={tpl.id} value={tpl.id}>{tpl.name}</option>
            ))}
          </select>
        </div>

        <Toggle
          checked={isOn}
          onChange={handleToggle}
          label={t('mambuMcpDetail.users.defaultAccess.toggleLabel')}
          disabled={templates.length === 0 && !isOn}
        />
      </div>
    </Card>
  )
}

/* ─── Card 2: Individual User Access ─── */

interface IndividualUserAccessCardProps {
  connectionId: string
  templates: PermissionTemplate[]
  userAccess: UserAccess[]
  tenantOperations: MambuConnectionOperation[]
  /** Drives the caption only — the list itself means something different in each state (§15). */
  defaultAccessOn: boolean
}


function IndividualUserAccessCard({ connectionId, templates, userAccess, tenantOperations, defaultAccessOn }: IndividualUserAccessCardProps) {
  const { t } = useTranslation()
  const apiFetch = useApiFetch()
  const qc = useQueryClient()
  const { treeOps, deprecatedTreeOps } = useMemo(
    () => splitOpsForTree(tenantOperations),
    [tenantOperations],
  )
  const userAccessKey = ['mambu-connections', connectionId, 'user-access']

  const [search, setSearch] = useState('')
  const [addOpen, setAddOpen] = useState(false)
  const [addError, setAddError] = useState<string | null>(null)
  const [addPrincipal, setAddPrincipal] = useState<PrincipalSearchItem | null>(null)
  const [addTemplateId, setAddTemplateId] = useState<string | null>(null)
  const [revokeConfirm, setRevokeConfirm] = useState(false)

  // Right-pane edit buffer. `selected` holds the full record so a freshly
  // created user can be shown before the query refetch lands.
  const [selected, setSelected] = useState<UserAccess | null>(null)
  const [editorTemplateId, setEditorTemplateId] = useState<string | null>(null)
  const [editorOps, setEditorOps] = useState<UserAccessOperation[]>([])
  const [saveError, setSaveError] = useState<string | null>(null)
  const [saveToast, setSaveToast] = useState<string | null>(null)

  useEffect(() => {
    if (!saveToast) return
    const id = setTimeout(() => setSaveToast(null), 2500)
    return () => clearTimeout(id)
  }, [saveToast])

  /* ── Entity-scope unsaved-changes protection (spec §7a) ──
     This editor commits its own entity and never dirties the page, so it needs its own
     baseline: without one, picking another user in the list silently discards the edit. */
  const [editorBaseline, setEditorBaseline] = useState<string | null>(null)
  const [pendingUser, setPendingUser] = useState<UserAccess | null>(null)
  const [closeRequested, setCloseRequested] = useState(false)

  function editorFingerprint(templateId: string | null, operations: UserAccessOperation[]): string {
    const enabled = operations.filter(o => o.isEnabled).map(o => o.operationId).sort()
    return JSON.stringify({ templateId, enabled })
  }

  const isEditorDirty = editorBaseline !== null
    && editorFingerprint(editorTemplateId, editorOps) !== editorBaseline

  // A templated grant stores no per-user operation rows of its own — the backend
  // resolves effective operations from the template at call time — so the display
  // must fall back to the applied template's operations rather than the (correctly
  // empty) `operations` field on the grant record itself.
  function displayOpsFor(u: { templateId: string | null; operations: UserAccessOperation[] }): UserAccessOperation[] {
    if (u.templateId) {
      const tpl = templates.find(tp => tp.id === u.templateId)
      return tpl ? tpl.operations.map(o => ({ ...o })) : []
    }
    return u.operations.map(o => ({ ...o }))
  }

  function selectUser(u: UserAccess) {
    const ops = displayOpsFor(u)
    setSelected(u)
    setEditorTemplateId(u.templateId)
    setEditorOps(ops)
    setSaveError(null)
    setEditorBaseline(editorFingerprint(u.templateId, ops))
  }

  /* Opening ANY user while the current one has unsaved edits must ask first (§7a).
     Deliberately not conditional on the clicked user differing from the open one:
     `selectUser` rebuilds the form from the saved entity either way, so re-opening the
     user already being edited discards the edits just as surely as switching away.
     The prototype guards the same way (agent-studio.html:11368-11371, "Covers re-opening
     the current user as well as switching to another"). */
  function requestSelectUser(u: UserAccess) {
    if (isEditorDirty && selected) {
      setPendingUser(u)
      return
    }
    selectUser(u)
  }

  /* Closes the editor panel entirely (deselects the user), discarding any unsaved edits. */
  function closeEditor() {
    setSelected(null)
    setEditorTemplateId(null)
    setEditorOps([])
    setSaveError(null)
    setEditorBaseline(null)
  }

  /* Cancel must close the form, asking first if there are unsaved edits (§7a) — the same
     guard already applied when switching to another user via requestSelectUser. */
  function requestCloseEditor() {
    if (isEditorDirty) {
      setCloseRequested(true)
      return
    }
    closeEditor()
  }

  useEntityEditorRegistration({
    isOpen: () => selected !== null,
    isDirty: () => isEditorDirty,
    markClean: () => closeEditor(),
  })

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase()
    if (!q) return userAccess
    return userAccess.filter(u =>
      u.principalDisplayName.toLowerCase().includes(q) ||
      (u.principalEmail?.toLowerCase().includes(q) ?? false),
    )
  }, [userAccess, search])

  const readOnlyOps = (): UserAccessOperation[] =>
    tenantOperations
      .filter(op => op.isEnabled && op.type === 'Read')
      .map(op => ({ operationId: op.id, isEnabled: true }))

  const createMutation = useMutation({
    mutationFn: (body: UserAccessCreate) =>
      apiFetch<UserAccess>(`/api/v1/mambu-connections/${connectionId}/user-access`, { method: 'POST', body: JSON.stringify(body) }),
    onSuccess: (created) => {
      void qc.invalidateQueries({ queryKey: userAccessKey })
      selectUser(created)
      closeAddModal()
      if (created.droppedOperationIds.length > 0) {
        setSaveToast(t('mambuMcpDetail.users.individualAccess.savedWithDroppedToast', { count: created.droppedOperationIds.length }))
      }
    },
    onError: (err: unknown) => {
      setAddError(err instanceof ApiError ? err.message : t('mambuMcpDetail.errors.failedToSave'))
    },
  })

  const deleteMutation = useMutation({
    mutationFn: (principalSub: string) =>
      apiFetch<void>(`/api/v1/mambu-connections/${connectionId}/user-access/${encodeURIComponent(principalSub)}`, { method: 'DELETE' }),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: userAccessKey })
      setSelected(null)
      setRevokeConfirm(false)
    },
  })

  const updateMutation = useMutation({
    mutationFn: (body: UserAccessUpdate) => {
      if (!selected) throw new Error('no user selected')
      return apiFetch<UserAccess>(`/api/v1/mambu-connections/${connectionId}/user-access/${encodeURIComponent(selected.principalSub)}`, { method: 'PUT', body: JSON.stringify(body) })
    },
    onSuccess: (updated) => {
      void qc.invalidateQueries({ queryKey: userAccessKey })
      setSelected(null)
      setEditorTemplateId(null)
      setEditorOps([])
      setSaveError(null)
      setEditorBaseline(null)
      setSaveToast(
        updated.droppedOperationIds.length > 0
          ? t('mambuMcpDetail.users.individualAccess.savedWithDroppedToast', { count: updated.droppedOperationIds.length })
          : t('mambuMcpDetail.users.individualAccess.savedToast'),
      )
    },
    onError: (err: unknown) => {
      setSaveError(err instanceof ApiError ? err.message : t('mambuMcpDetail.errors.failedToSave'))
    },
  })

  function openAddModal() {
    setAddOpen(true)
    setAddError(null)
    setAddPrincipal(null)
    setAddTemplateId(null)
  }

  function closeAddModal() {
    setAddOpen(false)
    setAddError(null)
    setAddPrincipal(null)
    setAddTemplateId(null)
  }

  function handleAddSelect(item: PrincipalSearchItem) {
    setAddPrincipal(item)
  }

  function handleAddSubmit() {
    if (!addPrincipal) return
    // The backend resolves a templated grant's operations from the template itself —
    // sending both a templateId and an operations list is rejected as a conflicting shape.
    createMutation.mutate({
      principalSub: addPrincipal.principalId,
      principalDisplayName: addPrincipal.principalName,
      principalEmail: addPrincipal.principalEmail,
      templateId: addTemplateId,
      operations: addTemplateId ? [] : readOnlyOps(),
    })
  }

  function handleApplyTemplate(templateId: string) {
    if (!templateId) {
      setEditorTemplateId(null)
      return
    }
    const tpl = templates.find(tp => tp.id === templateId)
    if (!tpl) return
    setEditorTemplateId(tpl.id)
    setEditorOps(tpl.operations.map(o => ({ ...o })))
  }

  function handleOpToggle(operationId: string, checked: boolean) {
    setEditorOps(prev => {
      const exists = prev.some(o => o.operationId === operationId)
      return exists
        ? prev.map(o => o.operationId === operationId ? { ...o, isEnabled: checked } : o)
        : [...prev, { operationId, isEnabled: checked }]
    })
  }

  function handleSave() {
    // Same server-side constraint as create: a templated grant carries no operations of its own.
    updateMutation.mutate({ templateId: editorTemplateId, operations: editorTemplateId ? [] : editorOps })
  }

  return (
    <>
    <Card
      title={t('mambuMcpDetail.users.individualAccess.cardTitle')}
      /* §15/§22a: the title is stable in both states; the caption switches. Two separate
         localised strings selected by state — never one string patched at runtime. */
      subtitle={defaultAccessOn
        ? t('mambuMcpDetail.users.individualAccess.cardSubtitleDefaultOn')
        : t('mambuMcpDetail.users.individualAccess.cardSubtitleDefaultOff')}
      noPadding
      className="editor-split-card"
    >
      <div className="editor-split-body">

        {/* ── Left: search + user list ── */}
        <div style={{ flex: 1, display: 'flex', flexDirection: 'column', minWidth: 0 }}>
          <div style={{ padding: '20px 24px 0' }}>
            <input
              type="search"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder={t('mambuMcpDetail.users.individualAccess.searchPlaceholder')}
              aria-label={t('mambuMcpDetail.users.individualAccess.searchPlaceholder')}
              className="form-input"
              style={{ maxWidth: '100%', fontSize: 12.5, padding: '6px 10px' }}
            />
          </div>
          <div style={{ flex: 1, padding: '12px 24px 20px', overflowY: 'auto' }}>
            {filtered.length === 0 ? (
              <div style={{ padding: 14, color: 'var(--text-tertiary)', fontSize: 12.5 }}>
                {t('mambuMcpDetail.users.individualAccess.noUsers')}
              </div>
            ) : (
              filtered.map(u => {
                return (
                  <div
                    key={u.principalSub}
                    className="skill-row mcp-user-row"
                    style={{ cursor: 'default' }}
                  >
                    <Avatar className="mcp-uavatar" size={16} style={{ width: 32, height: 32, fontSize: 11 }} />
                    <div className="skill-info" style={{ flex: 1, minWidth: 0 }}>
                      <div className="skill-name">{u.principalDisplayName}</div>
                      {u.principalEmail && <div className="skill-desc">{u.principalEmail}</div>}
                    </div>
                    <button
                      type="button"
                      className="btn-text skill-edit-btn"
                      aria-label={t('mambuMcpDetail.users.individualAccess.editAria')}
                      title={t('mambuMcpDetail.users.individualAccess.editAria')}
                      onClick={(e) => { e.stopPropagation(); requestSelectUser(u) }}
                    >
                      <PencilIcon size={16} />
                    </button>
                    <button
                      type="button"
                      className="btn-text skill-remove-btn"
                      aria-label={t('mambuMcpDetail.users.individualAccess.revokeAria', { name: u.principalDisplayName })}
                      title={t('mambuMcpDetail.users.individualAccess.revoke')}
                      onClick={(e) => { e.stopPropagation(); selectUser(u); setRevokeConfirm(true) }}
                    >
                      <TrashIcon />
                    </button>
                  </div>
                )
              })
            )}
          </div>
        </div>

        {/* separator */}
        <div style={{ width: 1, background: 'var(--border-light)', margin: '20px 0' }} />

        {/* ── Right: per-user editor ── */}
        <div className="editor-pane-col">
          {!selected ? (
            <div className="editor-pane-empty">
              <PeopleIcon />
              <p style={{ fontSize: 13 }}>{t('mambuMcpDetail.users.individualAccess.emptyStateTitle')}</p>
              <button type="button" className="btn btn-primary" onClick={openAddModal}>
                {t('mambuMcpDetail.users.individualAccess.addUser')}
              </button>
            </div>
          ) : (
            <div className="editor-form-wrap">
              <div className="editor-form-scroll">
                {/* Header: name + revoke */}
                <h3 style={{ fontSize: 15, fontWeight: 600, color: 'var(--text-primary)', margin: 0, marginBottom: 4, display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 12 }}>
                  {selected.principalDisplayName}
                  <button
                    type="button"
                    className="btn-text skill-remove-btn"
                    aria-label={t('mambuMcpDetail.users.individualAccess.revokeAria', { name: selected.principalDisplayName })}
                    onClick={() => setRevokeConfirm(true)}
                  >
                    <TrashIcon />
                  </button>
                </h3>
                {selected.principalEmail && (
                  <div style={{ fontSize: 12.5, color: 'var(--text-tertiary)', marginBottom: 14 }}>{selected.principalEmail}</div>
                )}

                {/* Apply permission template */}
                <div className="form-group">
                  <label htmlFor="mmcp-user-template" className="form-label">
                    <span style={{ display: 'inline-flex', alignItems: 'center', gap: 6 }}>
                      {t('mambuMcpDetail.users.individualAccess.applyTemplateLabel')}
                      <InfoTooltip width={260}>
                        {t('mambuMcpDetail.users.individualAccess.applyTemplateHint')}
                      </InfoTooltip>
                    </span>
                  </label>
                  <select
                    id="mmcp-user-template"
                    className="form-select"
                    value={editorTemplateId ?? ''}
                    onChange={(e) => handleApplyTemplate(e.target.value)}
                    style={{ maxWidth: 320 }}
                  >
                    <option value="">{t('mambuMcpDetail.users.individualAccess.noTemplate')}</option>
                    {templates.map(tpl => (
                      <option key={tpl.id} value={tpl.id}>{tpl.name}</option>
                    ))}
                  </select>
                </div>

                {/* Operations */}
                <div style={{ fontSize: 12, fontWeight: 600, textTransform: 'uppercase', letterSpacing: '.5px', color: 'var(--text-tertiary)', marginBottom: 10 }}>
                  {t('mambuMcpDetail.users.individualAccess.operationsLabel')}
                </div>
                <OperationsTree
                  ops={treeOps}
                  deprecatedOps={deprecatedTreeOps}
                  deprecatedGroupLabel={<><WarningIcon />{t('mambuMcpDetail.opsTree.deprecatedGroup')}</>}
                  mode="user"
                  isChecked={(id) => editorOps.some(o => o.operationId === id && o.isEnabled)}
                  onToggle={handleOpToggle}
                  isTenantEnabled={(id) => tenantOperations.find(op => op.id === id)?.isEnabled ?? false}
                  disabled={editorTemplateId !== null}
                  ariaLabel={t('mambuMcpDetail.opsTree.treeAriaLabel', { name: selected.principalDisplayName })}
                />

                {saveError && (
                  <div role="alert" style={{ fontSize: 12, color: 'var(--danger)', marginTop: 12 }}>{saveError}</div>
                )}
              </div>

              <div className="editor-form-bar" style={{ gap: 8, justifyContent: 'flex-end' }}>
                <button type="button" className="btn btn-ghost" onClick={requestCloseEditor} disabled={updateMutation.isPending} style={{ fontSize: 12, padding: '5px 12px' }}>
                  {t('mambuMcpDetail.users.individualAccess.cancel')}
                </button>
                <button type="button" className="btn btn-primary" onClick={handleSave} disabled={updateMutation.isPending} style={{ fontSize: 12, padding: '5px 12px', opacity: updateMutation.isPending ? 0.6 : 1 }}>
                  {t('mambuMcpDetail.users.individualAccess.save')}
                </button>
              </div>
            </div>
          )}
        </div>
      </div>

      {/* Add-user dialog */}
      {addOpen && (
        <div className="modal-backdrop" onClick={(e) => { if (e.target === e.currentTarget) closeAddModal() }}>
          <dialog open className="modal modal-overflow-visible" style={{ maxWidth: 440 }}>
            <div className="modal-header">
              <h3>{t('mambuMcpDetail.users.individualAccess.addUserModalTitle')}</h3>
            </div>
            <div className="modal-body">
              <p>{t('mambuMcpDetail.users.individualAccess.addUserModalSubtitle')}</p>
              <div className="form-group" style={{ position: 'relative' }}>
                <label className="form-label">
                  {t('mambuMcpDetail.users.individualAccess.addUserNameOrEmailLabel')}
                </label>
                <PrincipalSearch
                  type="User"
                  onSelect={handleAddSelect}
                  excludeIds={userAccess.map(u => u.principalSub)}
                />
                {addPrincipal && (
                  <div style={{ marginTop: 8, padding: '8px 12px', background: 'var(--accent-subtle)', border: '1px solid var(--accent)', borderRadius: 6, fontSize: 13, display: 'flex', alignItems: 'center', gap: 8 }}>
                    <div style={{ flex: 1, minWidth: 0 }}>
                      <div style={{ fontWeight: 600, fontSize: 13 }}>{addPrincipal.principalName}</div>
                      {addPrincipal.principalEmail && (
                        <div style={{ fontSize: 11, color: 'var(--text-tertiary)' }}>{addPrincipal.principalEmail}</div>
                      )}
                    </div>
                    <button
                      type="button"
                      aria-label={t('mambuMcpDetail.users.individualAccess.clearSelection')}
                      onClick={() => setAddPrincipal(null)}
                      className="icon-btn"
                      style={{ fontSize: 16, lineHeight: 1 }}
                    >
                      ×
                    </button>
                  </div>
                )}
              </div>

              <div className="form-group" style={{ marginBottom: 0 }}>
                <label htmlFor="mmcp-add-user-template" className="form-label">
                  {t('mambuMcpDetail.users.individualAccess.addUserTemplateLabel')}
                </label>
                <select
                  id="mmcp-add-user-template"
                  className="form-select"
                  value={addTemplateId ?? ''}
                  onChange={(e) => setAddTemplateId(e.target.value || null)}
                >
                  <option value="">{t('mambuMcpDetail.users.individualAccess.noTemplate')}</option>
                  {templates.map(tpl => (
                    <option key={tpl.id} value={tpl.id}>{tpl.name}</option>
                  ))}
                </select>
              </div>

              {addError && (
                <div role="alert" style={{ fontSize: 12, color: 'var(--danger)', marginTop: 8 }}>{addError}</div>
              )}
            </div>
            <div className="modal-footer">
              <button type="button" className="btn btn-ghost" onClick={closeAddModal}>
                {t('mambuMcpDetail.users.individualAccess.cancel')}
              </button>
              <button
                type="button"
                className="btn btn-primary"
                onClick={handleAddSubmit}
                disabled={!addPrincipal || createMutation.isPending}
              >
                {t('common.add')}
              </button>
            </div>
          </dialog>
        </div>
      )}

      {/* Entity-scope unsaved-changes guard — raised when the admin opens a different user,
          or cancels/closes the current one, while the open editor has unsaved edits (§7a). */}
      {(pendingUser || closeRequested) && (
        <UnsavedChangesDialog
          onConfirm={() => {
            if (pendingUser) {
              const next = pendingUser
              setPendingUser(null)
              selectUser(next)
            } else {
              setCloseRequested(false)
              closeEditor()
            }
          }}
          onCancel={() => { setPendingUser(null); setCloseRequested(false) }}
        />
      )}

      {/* Revoke / reset-to-default confirmation — meaning follows Tenant-Wide User Access (MS-734 AC8) */}
      {revokeConfirm && selected && (
        <ConfirmDialog
          title={defaultAccessOn
            ? t('mambuMcpDetail.users.individualAccess.resetTitle')
            : t('mambuMcpDetail.users.individualAccess.revokeTitle')}
          message={defaultAccessOn
            ? t('mambuMcpDetail.users.individualAccess.resetBody', { user: selected.principalDisplayName })
            : t('mambuMcpDetail.users.individualAccess.revokeBody', { user: selected.principalDisplayName })}
          confirmLabel={defaultAccessOn
            ? t('mambuMcpDetail.users.individualAccess.resetConfirm')
            : t('mambuMcpDetail.users.individualAccess.revokeConfirm')}
          danger={!defaultAccessOn}
          onConfirm={() => deleteMutation.mutate(selected.principalSub)}
          onCancel={() => setRevokeConfirm(false)}
        />
      )}
    </Card>
    {saveToast && <SuccessToast message={saveToast} />}
    </>
  )
}

/* ─── Tab ─── */

export function UsersTab({ connectionId, templates, userAccess, tenantOperations, defaultPermissionTemplateId, onDefaultPermissionTemplateIdChange }: UsersTabProps) {
  return (
    <>
      {/* Persistent (sticky) warning: user config applies to external MCP clients only */}
      <div
        className="warn-card"
        style={{ position: 'sticky', top: 0, zIndex: 6, marginBottom: 16, background: 'linear-gradient(rgba(255,193,7,.12),rgba(255,193,7,.12)),var(--bg-card)' }}
      >
        <WarningIcon size={16} />
        {/* Trans, not dangerouslySetInnerHTML: the copy carries a <strong>, which
            i18next renders natively (transKeepBasicHtmlNodesFor defaults to
            br/strong/i/p). security.md SEC-FE-3 requires every
            dangerouslySetInnerHTML to be DOMPurify-sanitized; this one was not. */}
        <span><Trans i18nKey="mambuMcpDetail.users.externalClientWarning" /></span>
      </div>
      <DefaultUserAccessCard
        templates={templates}
        defaultPermissionTemplateId={defaultPermissionTemplateId}
        onDefaultPermissionTemplateIdChange={onDefaultPermissionTemplateIdChange}
      />
      <IndividualUserAccessCard
        connectionId={connectionId}
        templates={templates}
        userAccess={userAccess}
        tenantOperations={tenantOperations}
        defaultAccessOn={defaultPermissionTemplateId !== null}
      />
    </>
  )
}
